import React from "react";
import { useState, useEffect, useContext, useRef } from "react";
import { useImmer } from "use-immer";
import { useTranslation } from "react-i18next";
import AuthContext from "../../contexts/AuthProvider";
import useThemeColors from "../../hooks/useThemeColors";

import { store } from "../../store";
import { ThreeDots } from "react-loader-spinner";
import moment from "moment/moment";
import cx from "classnames";
import { toast, Slide as ToastSlide } from "react-toastify";
import fileDownload from "js-file-download";
import { setProgress } from "../../reducers/fileTransferReducer";
import {
  client,
  fileUploadClient,
  fileDownloadClient,
} from "../../services/axiosClient";
import ReactSelect from "react-select";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import CircularProgress from "@material-ui/core/CircularProgress";

// material-ui icons
import {
  Alarm,
  Assessment,
  Assignment,
  AssignmentTurnedInRounded,
  Cached,
  CheckCircle,
  CheckRounded,
  CloudUploadRounded,
  DeleteForever,
  DoneAllRounded,
  ErrorOutline,
  FlashAutoRounded,
  Help,
  HourglassEmpty,
  Info,
  InsertDriveFile,
  LinkRounded,
  Memory,
  PlaylistAddCheckRounded,
  PlaylistPlay,
  TimerOff,
  WarningRounded,
  Settings,
} from "@material-ui/icons";

// core components
import GridContainer from "../../components/Grid/GridContainer.js";
import GridItem from "../../components/Grid/GridItem.js";
import Card from "../../components/Card/Card.js";
import CardBody from "../../components/Card/CardBody.js";
import CardIcon from "../../components/Card/CardIcon.js";
import CardHeader from "../../components/Card/CardHeader.js";
import Button from "../../components/CustomButtons/Button";
import FloatingLoading from "../../components/FloatingItems/FloatingLoading";
import CalculationLogViewer from "../../components/CalculationLog/CalculationLogViewer";
import SimpleLogViewer from "../../components/CalculationLog/SimpleLogViewer";
import SnackbarContent from "../../components/Snackbar/SnackbarContent";

import {
  blackColor,
  dangerColor,
  grayColor,
  infoColor,
  successColor,
  whiteColor,
  warningColor,
  blendColors,
} from "../../assets/jss/material-dashboard-pro-react";
import { CardContent, FormControl } from "@material-ui/core";
import TableHead from "@material-ui/core/TableHead";
import {
  inputsStyles,
  Transition,
  isExtensionValid,
  formatFileTypeLabel,
  ResetInputsDialog,
  InputNotesDialog,
} from "./inputs_components";
import { useUploadQueue } from "./inputs_components/hooks/useUploadQueue";
import { useSpecialOptionsModal } from "./inputs_components/hooks/useSpecialOptionsModal";
import UploadQueueDrawer from "./inputs_components/UploadQueueDrawer";
import SpecialOptionsModal from "./inputs_components/SpecialOptionsModal";
import InputDrawer from "./inputs_components/InputDrawer";
import MonthSelectorV2 from "./inputs_components/MonthSelectorV2";

/** InSights Demo implementation — must match `Implementation.implementation_name` in Django. */
const INSIGHTS_DEMO_IMPLEMENTATION_NAME = "Profit InSights";

export default function Inputs() {
  // region initial declarations
  let { userDetails } = useContext(AuthContext);
  const useStyles = makeStyles(inputsStyles);
  const classes = useStyles();
  const { clientColor } = useThemeColors();
  const { t, i18n } = useTranslation();

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "0",
      borderWidth: "0 0 1px 0",
      borderColor: state.isFocused ? clientColor : grayColor[7],
      marginTop: "-12px",
      boxShadow: "none",
      "&:hover": {
        borderColor: clientColor,
      },
    }),
    option: (provided, state) => ({
      ...provided,
      paddingTop: "3px",
      paddingBottom: "3px",
      backgroundColor: state.isSelected
        ? blendColors(whiteColor, clientColor, 0.8)
        : state.isFocused
          ? blendColors(whiteColor, clientColor, 0.9)
          : whiteColor,
      color: blackColor,
      "&:active": {
        backgroundColor: blendColors(whiteColor, clientColor, 0.85),
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: blackColor,
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: blendColors(whiteColor, clientColor, 0.85),
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: blackColor,
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: grayColor[0],
      "&:hover": {
        backgroundColor: blendColors(whiteColor, clientColor, 0.7),
        color: blackColor,
      },
    }),
    menu: (provided) => ({
      ...provided,
      marginTop: "2px",
      zIndex: "9999",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? clientColor : grayColor[7],
      "&:hover": { color: clientColor },
    }),
  };
  // endregion

  // region toasts
  const notify = (text, type = "info", time = 3000) =>
    toast(text, {
      position: "top-right",
      autoClose: time,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      theme: "light",
      type: type,
      transition: ToastSlide,
    });

  const notifyCopy = () =>
    toast(t("tasks.values_copied"), {
      position: "bottom-center",
      autoClose: 1200,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
      theme: "light",
      type: "info",
      transition: ToastSlide,
    });
  // endregion

  // region state declarations
  const [pipelineInProgress, setPipelineInProgress] = useState(false);
  const [monthlyStatus, setMonthlyStatus] = useImmer([]);
  const usersRef = useRef([]);
  const [responsibleUsers, setResponsibleUsers] = useState([]);
  const [filteredResponsibleUsers, setFilteredResponsibleUsers] = useState([]);
  const [myInputsFilter, setMyInputsFilter] = useState(false);
  const [pendingFilter, setPendingFilter] = useState(false);

  const [currentView, setCurrentView] = useState("detailed");
  const lastCurrentViewRef = useRef("detailed");
  const [showCalculationLog, setShowCalculationLog] = useState(false);
  const [calculationLog, setCalculationLog] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(false);
  const [selectedMonthData, setSelectedMonthData] = useState(null);
  const [inputsStatus, setInputsStatus] = useImmer([]);
  const [generalStatus, setGeneralStatus] = useState({});
  const [inputDrawerInput, setInputDrawerInput] = useImmer(null);
  const [
    inputDrawerShowExpectedFileContent,
    setInputDrawerShowExpectedFileContent,
  ] = useState(false);

  const [isRefreshing, setIsRefreshing] = useState(true);
  const controller = useRef(null);
  const autoRefreshTimeout = useRef(null);
  const lastRefreshParams = useRef("");
  const uploadingInputsRef = useRef(new Set());

  const [inputDetailsContentEdit, setInputDetailsContentEdit] = useState("");
  const [displayNotesModalOpen, setDisplayNotesModalOpen] = useState(false);
  const [resetInputsModalOpen, setResetInputsModalOpen] = useState(false);

  const [isSavingInputResponsible, setIsSavingInputResponsible] =
    useState(false);
  const [isSavingInputDescription, setIsSavingInputDescription] =
    useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [wrongDataType, setWrongDataType] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [replicatingFile, setReplicatingFile] = useState(false);

  const [downloadButtonPopperOpen, setDownloadButtonPopperOpen] =
    useState(null);
  const [downloadInProgress, setDownloadInProgress] = useState(null);

  const [
    inputControlsMarkedAsVerifiedLine,
    setInputControlsMarkedAsVerifiedLine,
  ] = useImmer([]);

  // endregion

  // region useEffect's
  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);
  useEffect(() => {
    if (selectedMonthData === null) {
      setSelectedMonth(Math.max(monthlyStatus.length - 1, 0));
      setSelectedMonthData(
        monthlyStatus.length > 1
          ? monthlyStatus[monthlyStatus.length - 1]
          : null,
      );
    }
  }, [monthlyStatus]);
  useEffect(() => {
    const pipeline = isPipelineInProgress();
    if (pipeline !== pipelineInProgress) setPipelineInProgress(pipeline);
  }, [inputsStatus]);

  // Atualiza o inputDrawerInput quando inputsStatus é atualizado via API
  useEffect(() => {
    if (inputDrawerInput && inputsStatus.length > 0) {
      // Não atualiza se há um upload em andamento
      if (isUploading) {
        return;
      }

      // Procura o input atual no drawer nos dados atualizados
      for (let i = 0; i < inputsStatus.length; i++) {
        for (let j = 0; j < inputsStatus[i].inputs.length; j++) {
          if (
            inputsStatus[i].inputs[j].input_id === inputDrawerInput.input_id
          ) {
            // Atualiza o inputDrawerInput com os dados mais recentes
            setInputDrawerInput(inputsStatus[i].inputs[j]);
            break;
          }
        }
      }
    }
  }, [inputsStatus]);

  useEffect(() => {
    const handle = setTimeout(() => {
      refreshInputsStatus();
    }, 500);
    return () => clearTimeout(handle);
  }, [selectedMonth]);
  useEffect(() => {
    // Só limpa o arquivo se não houver um arquivo selecionado aguardando envio
    if (fileName === "") {
      if (fileInputRef.current) fileInputRef.current.value = "";
      setWrongDataType(false);
    }
  }, [inputDrawerInput]);
  useEffect(() => {
    if (currentView === "general" || currentView === "responsible") {
      if (
        lastCurrentViewRef.current !== "general" &&
        lastCurrentViewRef.current !== "responsible"
      ) {
        setGeneralStatus({});
        client
          .post("api/inputs_status_all_periods/")
          .then((response) => {
            setGeneralStatus(response.data);
            lastCurrentViewRef.current = currentView;
          })
          .catch((error) => {
            console.log(error);
            lastCurrentViewRef.current = currentView;
          });
      }
    } else {
      lastCurrentViewRef.current = currentView;
    }
  }, [currentView]);
  useEffect(() => {
    const responsibleMap = new Map();
    for (const group of inputsStatus) {
      for (const input of group.inputs || []) {
        for (const resp of input.responsible || []) {
          const key = `${resp.id}-${resp.name}`;
          if (!responsibleMap.has(key)) {
            responsibleMap.set(key, resp);
          }
        }
      }
    }
    const sortedResponsibles = Array.from(responsibleMap.values()).sort(
      (a, b) => a.name.localeCompare(b.name),
    );
    setResponsibleUsers(sortedResponsibles);
  }, [inputsStatus]);

  useEffect(() => {
    i18n.changeLanguage(i18n.language.slice(0, 2));
    client
      .post("api/inputs_months_v2/")
      .then((response) => {
        usersRef.current = response.data.users;
        setMonthlyStatus(response.data.inputs);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  // endregion

  // region functions
  const getDate = (val, format_in, format_out = format_in) => {
    moment.locale(i18n.language);
    return moment(val, format_in).format(format_out).replace(".", "");
  };
  const checkIfAutoGeneratedInputsAreReady = (inputsStatus) => {
    for (const group of inputsStatus) {
      if (!Array.isArray(group.inputs)) continue;
      for (const input of group.inputs) {
        const dependencies = input.dependencies;
        const crossLinkingStage = input.stages?.cross_linking;
        if (
          dependencies !== null &&
          typeof dependencies === "object" &&
          Object.values(dependencies).every(
            (dep) =>
              Array.isArray(dep.data) &&
              dep.data.every((entry) => entry.ready === true),
          ) &&
          crossLinkingStage === "waiting"
        ) {
          return true;
        }
      }
    }
    return false;
  };
  const areAllRequiredInputsReady = (inputsStatus) => {
    let hasRequiredInputs = false;
    let allReady = true;
    for (const group of inputsStatus) {
      if (!Array.isArray(group.inputs)) continue;
      for (const input of group.inputs) {
        if (input.is_required) {
          hasRequiredInputs = true;
          const crossLinkingStage = input.stages?.cross_linking;
          if (
            crossLinkingStage !== "success" &&
            crossLinkingStage !== "warning"
          ) {
            allReady = false;
            break;
          }
        }
      }
      if (!allReady) break;
    }
    return hasRequiredInputs && allReady;
  };
  const isPipelineInProgress = (is = inputsStatus, md = selectedMonthData) => {
    if (md?.status === "running") return true;
    if (md?.pending_jobs_count > 0) return true;
    if (checkIfAutoGeneratedInputsAreReady(is)) return true;
    // If all required inputs are ready and status is "waiting", calculation should trigger soon
    if (md?.status === "waiting" && areAllRequiredInputsReady(is)) return true;
    return is.some((group) =>
      group.inputs?.some(
        (input) =>
          input.stages?.data_source === "running" ||
          input.stages?.data_structure === "running" ||
          input.stages?.cross_linking === "running",
      ),
    );
  };
  const hasSystemError = () => {
    if (selectedMonthData?.status === "system_error") return true;
    return inputsStatus.some((group) =>
      group.inputs?.some(
        (input) =>
          input.stages?.data_structure === "system_error" ||
          input.stages?.cross_linking === "system_error",
      ),
    );
  };
  const saveInputDetails = () => {
    if (isSavingInputDescription) return false;
    const params = {
      input_id: inputDrawerInput.input_id,
      description: inputDetailsContentEdit,
    };
    setIsSavingInputDescription(true);
    client
      .post("api/inputs_save_input_description/", params)
      .then(() => {
        // save monthly status
        let found = false;
        for (let i = 0; i < inputsStatus.length; i++) {
          for (let j = 0; j < inputsStatus[i].inputs.length; j++) {
            if (
              inputsStatus[i].inputs[j].input_id === inputDrawerInput.input_id
            ) {
              setInputsStatus((draft) => {
                draft[i].inputs[j].input_details = inputDetailsContentEdit;
              });
              found = true;
              break;
            }
          }
          if (found) break;
        }
        setIsSavingInputDescription(false);
        notify("Input description saved", "info", 2000);
      })
      .catch((error) => {
        console.log(error);
        setIsSavingInputDescription(false);
        notify("Error on saving input description", "error", 5000);
      });
  };
  const refreshInputsStatus = (silent = false) => {
    if (!selectedMonthData) return false;
    if (!silent) {
      setInputsStatus([]);
      setCalculationLog(null);
    }
    setIsRefreshing(true);
    const params = {
      month: selectedMonthData.month,
      year: selectedMonthData.year,
    };
    lastRefreshParams.current = JSON.stringify(params);
    clearTimeout(autoRefreshTimeout.current);
    if (controller.current) controller.current.abort();
    controller.current = new AbortController();
    client
      .post("api/inputs_status_v2/", params, {
        signal: controller.current.signal,
      })
      .then((response) => {
        if (JSON.stringify(params) === lastRefreshParams.current) {
          checkIfAutoGeneratedInputsAreReady(response.data.inputs);
          setInputsStatus(response.data.inputs);
          setCalculationLog(response.data.calculation_log || null);
          if (uploadingInputsRef.current.size > 0) {
            setInputsStatus((draft) => {
              for (let i = 0; i < draft.length; i++) {
                const inputs = draft[i].inputs || [];
                for (let j = 0; j < inputs.length; j++) {
                  const inp = inputs[j];
                  if (uploadingInputsRef.current.has(inp.input_control_id)) {
                    inp.stages.data_source = "running";
                    inp.stages.data_structure = "waiting";
                    inp.stages.cross_linking = "waiting";
                    inp.updated_by_username = userDetails.user_name;
                    inp.updated_in = new Date().toISOString().split("T")[0];
                  }
                }
              }
            });
          }
          setMonthlyStatus((draft) => {
            const index = draft.findIndex(
              (item) =>
                item.month === selectedMonthData.month &&
                item.year === selectedMonthData.year,
            );
            draft[index] = response.data.monthly_status;
          });
          setSelectedMonthData(response.data.monthly_status);
          if (
            isPipelineInProgress(
              response.data.inputs,
              response.data.monthly_status,
            )
          ) {
            autoRefreshTimeout.current = setTimeout(() => {
              refreshInputsStatus(true);
            }, 3000);
          } else {
            clearTimeout(autoRefreshTimeout.current);
          }
          setIsRefreshing(false);
        }
      })
      .catch((error) => {
        // Ignore canceled requests (user navigated away or component unmounted)
        if (error.name !== "CanceledError") {
          console.error("Error refreshing inputs:", error);
        }
        setIsRefreshing(false);
      });
  };
  const getAllGroups = () => {
    let groups = new Set();
    Object.values(generalStatus.inputs).forEach((month_data) => {
      Object.values(month_data).forEach((item) => {
        groups.add(item.group_meta_name);
      });
    });
    return [...groups];
  };
  const getInputsFromGroup = (group_name, count = false) => {
    let inputs = new Set();
    Object.values(generalStatus.inputs).forEach((month_data) => {
      Object.values(month_data).forEach((item) => {
        if (item.group_meta_name === group_name) {
          item.inputs.forEach((input) => {
            inputs.add(input.input_id);
          });
        }
      });
    });
    if (count) {
      return inputs.size;
    } else {
      return [...inputs];
    }
  };
  const generalStatusTag = (year_month, group, input, model) => {
    if (model) {
      return (
        <div
          className={cx({
            [classes.generalStatusTag]: true,
            [model]: true,
          })}
        />
      );
    }
    for (const [ym, month_data] of Object.entries(generalStatus.inputs)) {
      if (parseInt(ym) === year_month) {
        for (const group_data of month_data) {
          if (group_data.group_meta_name === group) {
            for (const input_data of group_data.inputs) {
              if (input_data.input_id === input) {
                const { data_source, data_structure, cross_linking } =
                  input_data;
                return (
                  <div
                    className={cx({
                      [classes.generalStatusTag]: true,
                      ["waiting"]: data_source === "waiting",
                      ["success1"]:
                        data_source === "success" &&
                        (data_structure === "waiting" ||
                          cross_linking === "waiting") &&
                        !["warning", "error", "system_error"].includes(
                          data_structure,
                        ) &&
                        !["warning", "error", "system_error"].includes(
                          cross_linking,
                        ),
                      ["success2"]:
                        data_source === "success" &&
                        ![
                          "waiting",
                          "warning",
                          "error",
                          "system_error",
                        ].includes(data_structure) &&
                        ![
                          "waiting",
                          "warning",
                          "error",
                          "system_error",
                        ].includes(cross_linking),
                      ["warning"]:
                        data_source === "success" &&
                        (data_structure === "warning" ||
                          cross_linking === "warning") &&
                        !["error", "system_error"].includes(data_structure) &&
                        !["error", "system_error"].includes(cross_linking),
                      ["error"]:
                        data_source === "success" &&
                        (["error", "system_error"].includes(data_structure) ||
                          ["error", "system_error"].includes(cross_linking)),
                    })}
                  />
                );
              }
            }
          }
        }
      }
    }
    return null;
  };
  const isInputPending = (input) => {
    if (input.stages.data_source !== "success") return true;
    if (
      ["error", "system_error"].includes(input.stages.data_structure) ||
      ["error", "system_error"].includes(input.stages.cross_linking)
    )
      return true;
    if (
      input.stages.data_structure === "warning" ||
      input.stages.cross_linking === "warning"
    )
      return true;
    return false;
  };
  const getFilteredInputsStatus = () => {
    let result = inputsStatus;
    if (filteredResponsibleUsers.length > 0) {
      const filteredIds = filteredResponsibleUsers.map((user) => user.id);
      let filtered = [];
      result.forEach((group) => {
        let filteredGroup = {
          group_name: group.group_name,
          inputs: [],
        };
        group.inputs.forEach((input) => {
          if (
            Array.isArray(input.responsible) &&
            input.responsible
              .map((resp) => resp.id)
              .some((resp) => filteredIds.includes(resp))
          ) {
            filteredGroup.inputs.push(input);
          }
        });
        if (filteredGroup.inputs.length > 0) filtered.push(filteredGroup);
      });
      result = filtered;
    }
    if (pendingFilter) {
      let filtered = [];
      result.forEach((group) => {
        let filteredGroup = {
          group_name: group.group_name,
          inputs: group.inputs.filter(isInputPending),
        };
        if (filteredGroup.inputs.length > 0) filtered.push(filteredGroup);
      });
      result = filtered;
    }
    return result;
  };
  // endregion

  // region handlers
  const handleConnectionClick = (conn) => {
    setInputDrawerShowExpectedFileContent(false);
    setFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    setWrongDataType(false);
    setInputDrawerInput(conn);
  };
  const handleShowExpectedFileContentClick = (e) => {
    e.stopPropagation();
    setInputDrawerShowExpectedFileContent((cur) => !cur);
  };
  const handleConfirmResetInput = () => {
    setInputsStatus([]);
    setIsRefreshing(true);
    let params = {
      month: selectedMonthData.month,
      year: selectedMonthData.year,
    };
    client
      .post("api/inputs_reset_month_inputs/", params)
      .then(() => {
        setResetInputsModalOpen(false);
        refreshInputsStatus();
      })
      .catch((error) => {
        console.log(error);
        setIsRefreshing(false);
      });
  };
  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };
  const handleFileSelect = (file) => {
    const fileExtension = file.name
      .substring(file.name.lastIndexOf(".") + 1)
      .toLowerCase();

    setWrongDataType(
      !isExtensionValid(fileExtension, inputDrawerInput.expected_file_type),
    );
    setFileName(file.name);
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    fileInputRef.current.files = dataTransfer.files;
  };
  // Atualiza o status do input otimisticamente (antes da resposta do backend)
  const updateInputStatusOptimistically = (inputControlId, start = false) => {
    if (start) {
      uploadingInputsRef.current.add(inputControlId);
    }
    setInputsStatus((draft) => {
      for (let i = 0; i < draft.length; i++) {
        for (let j = 0; j < draft[i].inputs.length; j++) {
          if (draft[i].inputs[j].input_control_id === inputControlId) {
            draft[i].inputs[j].stages.data_source = start
              ? "running"
              : "success";
            draft[i].inputs[j].stages.data_structure = start
              ? "waiting"
              : "running";
            draft[i].inputs[j].stages.cross_linking = "waiting";
            draft[i].inputs[j].updated_by_username = userDetails.user_name;
            draft[i].inputs[j].updated_in = new Date()
              .toISOString()
              .split("T")[0];
            return;
          }
        }
      }
    });
  };

  const {
    queueDrawerOpen,
    setQueueDrawerOpen,
    uploadQueueItems,
    setUploadQueueItems,
    queueRunning,
    setQueueRunning,
    setQueuePauseAfterCurrent,
    queueIgnoreUploaded,
    setQueueIgnoreUploaded,
    queueFolderInputRef,
    queueFileInputRef,
    queuePendingInputIdRef,
    queueNewSelectedInputId,
    setQueueNewSelectedInputId,
    queueSelectResetKey,
    setQueueSelectResetKey,
    canRunQueue,
    isQueueActive,
    addItemToQueue,
    removeItemFromQueue,
    moveItemUp,
    moveItemDown,
    clearQueue,
    handleQueuePickFile,
    handleQueueFolderSelected,
  } = useUploadQueue({
    userDetails,
    selectedMonthData,
    inputsStatus,
    uploadingInputsRef,
    updateInputStatusOptimistically,
    refreshInputsStatus,
  });

  const specialOptionsApi = useSpecialOptionsModal({
    monthlyStatus,
    refreshInputsStatus,
    showUpdateDemoTab:
      userDetails?.implementation_name === INSIGHTS_DEMO_IMPLEMENTATION_NAME &&
      userDetails?.user_role === "DEV",
  });

  useEffect(() => {
    if (monthlyStatus.length > 0 && userDetails?.user_role === "DEV") {
      specialOptionsApi.checkActiveBatchJob();
    }
  }, [monthlyStatus.length, userDetails?.user_role]);

  const handleReplicateFileClick = () => {
    setIsUploading(true);
    setReplicatingFile(true);
    let params = {
      input_control_id: inputDrawerInput.input_control_id,
    };

    updateInputStatusOptimistically(inputDrawerInput.input_control_id, true);
    client
      .post("api/inputs_replicate_file/", params)
      .then((response) => {
        if (response.data.success) {
          setIsUploading(false);
          uploadingInputsRef.current.delete(inputDrawerInput.input_control_id);
          updateInputStatusOptimistically(inputDrawerInput.input_control_id);
          setReplicatingFile(false);
          setInputDrawerInput(null);
          notify(t("modeling_inputs_v2.upload_completed"), "success");
          refreshInputsStatus(true); // silent refresh
        } else {
          setIsUploading(false);
          uploadingInputsRef.current.delete(inputDrawerInput.input_control_id);
          setReplicatingFile(false);
          notify(
            t("modeling_inputs_v2.errors." + response.data.error_code),
            "error",
            5000,
          );
          // Em caso de erro, refaz o refresh completo para corrigir o estado
          refreshInputsStatus();
        }
      })
      .catch((error) => {
        setIsUploading(false);
        setReplicatingFile(false);
        console.log(error);
        // Em caso de erro, refaz o refresh completo para corrigir o estado
        refreshInputsStatus();
      });
  };
  const handleUploadFileClick = () => {
    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", fileInputRef.current.files[0]);
    formData.append("input_control_id", inputDrawerInput.input_control_id);

    updateInputStatusOptimistically(inputDrawerInput.input_control_id, true);
    client.post("api/awake/").then(() => {
      fileUploadClient
        .post("api/inputs_upload/", formData)
        .then((response) => {
          if (response.data.success) {
            setIsUploading(false);
            uploadingInputsRef.current.delete(
              inputDrawerInput.input_control_id,
            );
            updateInputStatusOptimistically(inputDrawerInput.input_control_id);
            setInputDrawerInput(null);
            notify(t("modeling_inputs_v2.upload_completed"), "success");
            refreshInputsStatus(true); // silent refresh
          } else {
            setIsUploading(false);
            store.dispatch(setProgress(0));
            notify(
              t("modeling_inputs_v2.errors." + response.data.error_code),
              "error",
              5000,
            );
            // Em caso de erro, refaz o refresh completo para corrigir o estado
            refreshInputsStatus();
          }
        })
        .catch((error) => {
          setIsUploading(false);
          uploadingInputsRef.current.delete(inputDrawerInput.input_control_id);
          store.dispatch(setProgress(0));
          console.log(error);
          // Em caso de erro, refaz o refresh completo para corrigir o estado
          refreshInputsStatus();
        });
    });
  };
  const handleViewNotesClick = () => {
    if (inputDrawerInput.notes.length > 0) {
      setDisplayNotesModalOpen(true);
    } else {
      notify(t("modeling_inputs_v2.no_notes_notification"), "info", 2000);
    }
  };
  const handleDownloadLastUploadedFileButton = (event) => {
    if (
      downloadButtonPopperOpen &&
      downloadButtonPopperOpen.contains(event.target)
    ) {
      setDownloadButtonPopperOpen(null);
    } else {
      setDownloadButtonPopperOpen(event.currentTarget);
    }
  };
  const handleRetryProcessingButtonClick = () => {
    const params = {
      input_control_id: inputDrawerInput.input_control_id,
    };
    setIsUploading(true);
    setReplicatingFile(true);

    updateInputStatusOptimistically(inputDrawerInput.input_control_id, true);
    client
      .post("api/inputs_retry_input/", params)
      .then((response) => {
        if (response.data.success) {
          setIsUploading(false);
          uploadingInputsRef.current.delete(inputDrawerInput.input_control_id);
          updateInputStatusOptimistically(inputDrawerInput.input_control_id);
          setReplicatingFile(false);
          setInputDrawerInput(null);
          notify(t("modeling_inputs_v2.reprocess_requested"), "info", 2000);
          refreshInputsStatus(true);
        } else {
          setIsUploading(false);
          uploadingInputsRef.current.delete(inputDrawerInput.input_control_id);
          setReplicatingFile(false);
          notify(
            t("modeling_inputs_v2.errors." + response.data.error_code),
            "error",
            5000,
          );
          refreshInputsStatus();
        }
      })
      .catch((error) => {
        setIsUploading(false);
        uploadingInputsRef.current.delete(inputDrawerInput.input_control_id);
        setReplicatingFile(false);
        console.log(error);
        refreshInputsStatus();
      });
  };
  const handleDownloadFileSelection = (option) => {
    if (downloadInProgress !== null) return false;
    const params = {
      input_control_id: inputDrawerInput.input_control_id,
      option,
    };
    setDownloadInProgress(option);
    client.post("api/awake/").then(() => {
      fileDownloadClient
        .post("api/inputs_download/", params)
        .then((response) => {
          fileDownload(
            response.data,
            response.headers["content-disposition"].split("filename=")[1],
          );
          setDownloadButtonPopperOpen(null);
          setDownloadInProgress(null);
        })
        .catch((error) => {
          console.log(error);
          setDownloadButtonPopperOpen(null);
          setDownloadInProgress(null);
        });
    });
  };
  const handleClickAwayDownloadFileSelection = (event) => {
    if (downloadInProgress !== null) return false;
    if (typeof event.target.className === "string") {
      if (event.target.className.includes("dropdownItem")) return false;
    }
    setDownloadButtonPopperOpen(null);
  };
  const handleRefreshInputsClick = () => {
    if (isRefreshing) return false;
    refreshInputsStatus(true);
  };
  const handleResetInputsClick = () => {
    if (isRefreshing || inputsStatus.length === 0) return false;
    setResetInputsModalOpen(true);
  };
  const handleMarkAsAnalyzedClick = (uuid) => {
    setInputControlsMarkedAsVerifiedLine((draft) => {
      draft.push(uuid);
    });
    const params = { uuid };
    client
      .post("api/inputs_note_mark_as_verified/", params)
      .then((response) => {
        setInputsStatus((draft) => {
          draft.forEach((item) => {
            item.inputs.forEach((input) => {
              input.notes.forEach((note) => {
                if (note.uuid === uuid) {
                  note.marked_as_verified = true;
                  note.marked_as_verified_by = response.data.username;
                  if (response.data.change_to_success_status) {
                    if (response.data.check_type === "STR") {
                      input.stages.data_structure = "success";
                    } else if (response.data.check_type === "CSL") {
                      input.stages.cross_linking = "success";
                    }
                  }
                }
              });
            });
          });
        });
        setInputDrawerInput((draft) => {
          const note = draft.notes.find((note) => note.uuid === uuid);
          if (note) {
            note.marked_as_verified = true;
            note.marked_as_verified_by = response.data.username;
          }
          if (response.data.change_to_success_status) {
            if (response.data.check_type === "STR") {
              draft.stages.data_structure = "success";
            } else if (response.data.check_type === "CSL") {
              draft.stages.cross_linking = "success";
            }
          }
        });
        setInputControlsMarkedAsVerifiedLine((draft) => {
          const index = draft.indexOf(uuid);
          if (index !== -1) draft.splice(index, 1);
        });
      })
      .catch((error) => {
        setInputControlsMarkedAsVerifiedLine((draft) => {
          const index = draft.indexOf(uuid);
          if (index !== -1) draft.splice(index, 1);
        });
        console.log(error);
      });
  };
  const handleResponsibleSelectionChange = (e) => {
    setInputDrawerInput((draft) => {
      let resp = [];
      for (let i = 0; i < e.length; i++) {
        resp.push({
          id: e[i].value,
          name: e[i].label,
        });
      }
      draft.responsible = resp;
    });
  };
  const handleSaveResponsibleButtonClick = () => {
    if (isSavingInputResponsible) return false;
    const params = {
      input_id: inputDrawerInput.input_id,
      responsible: inputDrawerInput.responsible.map((r) => r.id),
    };
    setIsSavingInputResponsible(true);
    client
      .post("api/inputs_save_responsible/", params)
      .then(() => {
        let found = false;
        for (let i = 0; i < inputsStatus.length; i++) {
          for (let j = 0; j < inputsStatus[i].inputs.length; j++) {
            if (
              inputsStatus[i].inputs[j].input_id === inputDrawerInput.input_id
            ) {
              setInputsStatus((draft) => {
                draft[i].inputs[j].responsible = inputDrawerInput.responsible;
              });
              found = true;
              break;
            }
          }
          if (found) break;
        }
        notify(t("modeling_inputs_v2.responsible_saved"), "success", 2000);
        setIsSavingInputResponsible(false);
      })
      .catch((error) => {
        const error_code = error.response.data?.error_code;
        notify(
          t(error_code ? "groups.errors." + error_code : "server_error"),
          "danger",
          3000,
        );
        setIsSavingInputResponsible(false);
      });
  };
  const handleMyInputsToggle = () => {
    if (!myInputsFilter) {
      const currentUser = usersRef.current.find(
        (u) => u.name === userDetails.name,
      );
      if (currentUser) {
        setFilteredResponsibleUsers([currentUser]);
        setMyInputsFilter(true);
      } else {
        notify(
          t("modeling_inputs_v2.no_inputs_assigned") ||
            "Você não possui inputs atribuídos",
          "warning",
        );
      }
    } else {
      setFilteredResponsibleUsers([]);
      setMyInputsFilter(false);
    }
  };
  const handlePendingToggle = () => {
    setPendingFilter((prev) => !prev);
  };
  const handleFilterResponsibleSelectionChange = (e) => {
    let resp = [];
    for (let i = 0; i < e.length; i++) {
      resp.push({
        id: e[i].value,
        name: e[i].label,
      });
    }
    setFilteredResponsibleUsers(resp);
    if (myInputsFilter) setMyInputsFilter(false);
  };
  // endregion

  // region component fragments
  const monthSelectionGrid = () => {
    return (
      <MonthSelectorV2
        monthlyStatus={monthlyStatus}
        selectedMonth={selectedMonth}
        onMonthSelect={(index) => {
          if (isUploading || isQueueActive) {
            notify(t("modeling_inputs_v2.please_wait"), "warning");
            return;
          }
          setSelectedMonth(index);
          setSelectedMonthData(monthlyStatus[index]);
          setUploadQueueItems([]);
          setQueueDrawerOpen(false);
          setQueuePauseAfterCurrent(false);
        }}
        disabled={isUploading || isQueueActive}
      />
    );
  };
  const getMonthProgressIcon = (progress) => {
    switch (progress) {
      case "verifying":
        return <HourglassEmpty />;
      case "unknown":
        return <Help className={classes.chipIcon} />;
      case "waiting":
        return <Alarm />;
      case "running":
        return <Memory />;
      case "complete":
        return <CheckCircle className={classes.chipIcon} />;
      case "error":
        return <ErrorOutline className={classes.chipIcon} />;
      case "system_error":
        return <TimerOff className={classes.chipIcon} />;
    }
  };
  const inputBox = (input, key) => {
    return (
      // Outer box
      <div
        key={key}
        className={cx({
          [classes.inputBox]: true,
          ["error"]: Object.values(input.stages).some(
            (element) => element === "system_error",
          ),
          ["running"]: Object.values(input.stages).some(
            (element) => element === "running",
          ),
        })}
        onClick={() => handleConnectionClick(input)}
      >
        {/* Input name */}
        <div
          style={{
            position: "absolute",
            top: "6px",
            left: "6px",
            right: "6px",
            textAlign: "center",
            fontSize: "12px",
            lineHeight: "1.25",
          }}
        >
          {input.input_name[i18n.language]}
        </div>
        {/* File icon */}
        <div
          style={{
            position: "absolute",
            textAlign: "center",
            top: "52%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            width: "78px",
            // color: grayColor[1],
          }}
        >
          <InsertDriveFile
            style={{
              height: "100%",
              width: "100%",
              opacity: "0.6",
            }}
          />
        </div>
        {/* File type */}
        <div
          style={{
            position: "absolute",
            textAlign: "center",
            top: "62%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            color: whiteColor,
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {formatFileTypeLabel(input.expected_file_type)}
        </div>
        {/* Data source status */}
        <Tooltip title={t("modeling_inputs_v2.data_source")}>
          <div
            className={cx({
              [classes.fileStatus]: true,
              [input.stages.data_source]: true,
            })}
            style={{ left: "28%" }}
          >
            {input.source === "file_upload" && (
              <CloudUploadRounded
                style={{
                  height: "80%",
                  width: "80%",
                }}
              />
            )}
            {input.source === "auto_generated" && (
              <PlaylistAddCheckRounded
                style={{
                  height: "80%",
                  width: "80%",
                }}
              />
            )}
          </div>
        </Tooltip>
        {/* Data structure status */}
        <Tooltip title={t("modeling_inputs_v2.data_structure")}>
          <div
            className={cx({
              [classes.fileStatus]: true,
              [input.stages.data_structure]: true,
            })}
            style={{ left: "50%" }}
          >
            <AssignmentTurnedInRounded
              style={{
                height: "80%",
                width: "80%",
              }}
            />
          </div>
        </Tooltip>
        {/* Cross-linking status */}
        <Tooltip title={t("modeling_inputs_v2.cross_linking")}>
          <div
            className={cx({
              [classes.fileStatus]: true,
              [input.stages.cross_linking]: true,
            })}
            style={{ left: "72%" }}
          >
            <LinkRounded
              style={{
                height: "80%",
                width: "80%",
              }}
            />
          </div>
        </Tooltip>
        {/* Readiness icons */}
        <div
          style={{
            position: "absolute",
            right: "10px",
            top: "48%",
            transform: "translate(0,-50%)",
            width: "32px",
            height: "32px",
          }}
        >
          {input.source === "auto_generated" ? (
            <Tooltip title={t("modeling_inputs_v2.auto_generated_input")}>
              <FlashAutoRounded
                style={{
                  color: infoColor[7],
                  height: "100%",
                  width: "100%",
                }}
              />
            </Tooltip>
          ) : (
            <>
              {input.stages.data_source === "success" &&
                ["success", "warning"].includes(input.stages.data_structure) &&
                input.stages.cross_linking === "waiting" && (
                  <Tooltip title={t("modeling_inputs_v2.file_sent")}>
                    <CheckRounded
                      style={{
                        color: successColor[3],
                        height: "100%",
                        width: "100%",
                      }}
                    />
                  </Tooltip>
                )}
              {["success", "warning"].includes(input.stages.cross_linking) && (
                <Tooltip title={t("modeling_inputs_v2.file_sent_and_checked")}>
                  <DoneAllRounded
                    style={{
                      color: successColor[3],
                      height: "100%",
                      width: "100%",
                    }}
                  />
                </Tooltip>
              )}
              {(["error", "system_error"].includes(
                input.stages.data_structure,
              ) ||
                ["error", "system_error"].includes(
                  input.stages.cross_linking,
                )) && (
                <Tooltip title={t("modeling_inputs_v2.data_invalidated")}>
                  <WarningRounded
                    style={{
                      color: dangerColor[3],
                      height: "100%",
                      width: "100%",
                    }}
                  />
                </Tooltip>
              )}
              {input.stages.data_source === "waiting" &&
                input.available_data_from && (
                  <Tooltip
                    title={
                      t("modeling_inputs_v2.available_data_from") +
                      " " +
                      t("months." + input.available_data_from[0].toString()) +
                      "-" +
                      input.available_data_from[1].toString().slice(-2)
                    }
                  >
                    <Cached
                      style={{
                        color: successColor[3],
                        opacity: "0.6",
                        height: "100%",
                        width: "100%",
                      }}
                    />
                  </Tooltip>
                )}
            </>
          )}
        </div>
      </div>
    );
  };
  const inputsComponent = () => {
    return getFilteredInputsStatus().map((input_group, key1) => {
      return (
        <Card
          key={key1}
          style={{
            minWidth: "1200px",
            marginTop: "8px",
            marginBottom: "0px",
          }}
        >
          <CardBody
            style={{
              padding: "0 10px 3px 10px",
            }}
          >
            <div
              style={{
                color: grayColor[3],
                backgroundColor: grayColor[8] + "44",
                margin: "0 -10px 10px -10px",
                borderBottom:
                  "1px solid " + blendColors(clientColor, whiteColor, 0.25),
                textAlign: "center",
                borderRadius: "6px 6px 0 0",
                lineHeight: "2",
              }}
            >
              {input_group.group_name[i18n.language]}
            </div>
            <div
              style={{
                marginTop: "8px",
              }}
            >
              {input_group.inputs.map((input, key2) => {
                return inputBox(input, key2);
              })}
            </div>
          </CardBody>
        </Card>
      );
    });
  };
  const monthlyStatusDiv = () => {
    return (
      <div className={classes.monthlyStatus}>
        <div
          style={{
            borderRight: "1px solid " + grayColor[0],
            paddingRight: "8px",
            marginRight: "8px",
          }}
        >
          {t("months." + selectedMonthData.month.toString()) +
            "-" +
            selectedMonthData.year.toString().slice(-2)}
        </div>
        {t("modeling_inputs_v2.month_status.month_calculation_progress")}
        {":"}
        <Tooltip
          title={t(
            "modeling_inputs_v2.month_status.tooltip." +
              selectedMonthData.status,
          )}
        >
          <Chip
            style={{ marginLeft: "8px", marginBottom: "2px" }}
            className={cx({
              [classes.chipWarning]: selectedMonthData.status === "unknown",
              [classes.chipWaiting]:
                selectedMonthData.status === "verifying" ||
                selectedMonthData.status === "waiting",
              [classes.chipInProgress]: selectedMonthData.status === "running",
              [classes.chipError]:
                selectedMonthData.status === "system_error" ||
                selectedMonthData.status === "error",
              [classes.chipSuccess]: selectedMonthData.status === "complete",
            })}
            size="small"
            icon={getMonthProgressIcon(selectedMonthData.status)}
            label={t(
              "modeling_inputs_v2.month_status." + selectedMonthData.status,
            )}
          />
        </Tooltip>
        {hasSystemError() && (
          <Chip
            style={{ marginLeft: "8px", marginBottom: "2px" }}
            className={classes.chipError}
            size="small"
            label={t("modeling_inputs_v2.contact_integration_team")}
          />
        )}
        <span
          style={{
            marginLeft: "12px",
            marginRight: "12px",
            color: grayColor[0],
          }}
        >
          {t("modeling_inputs_v2.total_inputs") +
            ": " +
            selectedMonthData.total_inputs}
          {selectedMonthData.pending_jobs_count > 0 && (
            <>
              {" | " + t("modeling_inputs_v2.processing") + ": "}
              {selectedMonthData.pending_jobs_count}
            </>
          )}
        </span>
        <Tooltip title={t("modeling_inputs_v2.refresh")}>
          <div
            className={cx({
              [classes.inlineButton]: true,
              ["disabled"]: isRefreshing || inputsStatus.length === 0,
            })}
            onClick={() => handleRefreshInputsClick()}
          >
            {!isRefreshing && <Cached />}
            {isRefreshing && (
              <ThreeDots
                color={grayColor[0]}
                height={"8px"}
                width={"30px"}
                wrapperStyle={{
                  height: "8px",
                  width: "30px",
                }}
              />
            )}
          </div>
        </Tooltip>
        {userDetails?.user_role === "DEV" && (
          <Tooltip title={t("modeling_inputs_v2.upload_queue")}>
            <div
              className={cx({
                [classes.inlineButton]: true,
                ["disabled"]: queueRunning,
              })}
              onClick={() => {
                if (queueRunning) return;
                setQueueDrawerOpen(true);
              }}
            >
              <PlaylistPlay />
            </div>
          </Tooltip>
        )}
        {userDetails?.user_role === "DEV" && (
          <Tooltip
            title={t("modeling_inputs_v2.batch_operations.special_options")}
          >
            <div
              className={cx({
                [classes.inlineButton]: true,
                ["disabled"]: isRefreshing || inputsStatus.length === 0,
                ["active"]: specialOptionsApi.specialOptionsModalOpen,
              })}
              style={
                specialOptionsApi.specialOptionsModalOpen
                  ? { backgroundColor: "rgba(0,0,0,0.1)" }
                  : {}
              }
              onClick={() => {
                if (isRefreshing || inputsStatus.length === 0) return;
                specialOptionsApi.handleOpenSpecialOptionsModal();
              }}
            >
              <Settings />
            </div>
          </Tooltip>
        )}
        {userDetails?.user_role !== "DEV" && (
          <Tooltip title={t("modeling_inputs_v2.restart_month_modeling")}>
            <div
              className={cx({
                [classes.inlineButton]: true,
                ["disabled"]:
                  isRefreshing ||
                  inputsStatus.length === 0 ||
                  isPipelineInProgress(),
              })}
              onClick={() => handleResetInputsClick()}
            >
              <DeleteForever />
            </div>
          </Tooltip>
        )}
        {["DEV", "IMG", "CAD"].includes(userDetails?.user_role) && (
          <Tooltip
            title={t("modeling_inputs_v2.calculation_log") || "Log de Cálculo"}
          >
            <div
              className={cx({
                [classes.inlineButton]: true,
                ["disabled"]: isRefreshing || inputsStatus.length === 0,
                ["active"]: showCalculationLog,
              })}
              style={
                showCalculationLog ? { backgroundColor: "rgba(0,0,0,0.1)" } : {}
              }
              onClick={() => setShowCalculationLog(!showCalculationLog)}
            >
              <Assessment />
            </div>
          </Tooltip>
        )}
      </div>
    );
  };

  const generalStatusDiv = () => {
    return (
      <Card
        style={{
          minWidth: "1200px",
          marginTop: "8px",
        }}
      >
        <CardContent
          style={{
            padding: "60px 0 0 0",
            position: "relative",
          }}
        >
          <div className={classes.generalViewLegend}>
            {generalStatusTag(null, null, null, "waiting")}
            <span style={{ marginLeft: "4px", marginRight: "8px" }}>
              {t("modeling_inputs_v2.global_view.not_sent")}
            </span>
            {generalStatusTag(null, null, null, "success1")}
            <span style={{ marginLeft: "4px", marginRight: "8px" }}>
              {t("modeling_inputs_v2.global_view.system_checks_pending")}
            </span>
            {generalStatusTag(null, null, null, "success2")}
            <span style={{ marginLeft: "4px", marginRight: "8px" }}>
              {t("modeling_inputs_v2.global_view.no_points_of_attention")}
            </span>
            {generalStatusTag(null, null, null, "warning")}
            <span style={{ marginLeft: "4px", marginRight: "8px" }}>
              {t("modeling_inputs_v2.global_view.with_points_of_attention")}
            </span>
            {generalStatusTag(null, null, null, "error")}
            <span style={{ marginLeft: "4px" }}>
              {t("modeling_inputs_v2.global_view.file_with_error")}
            </span>
          </div>
          <TableContainer className={classes.generalViewTable}>
            <Table size="small" /*stickyHeader*/>
              <TableHead className={classes.generalViewTableHeader}>
                <TableRow>
                  <TableCell>
                    {t("modeling_inputs_v2.global_view.group")}
                  </TableCell>
                  <TableCell>
                    {t("modeling_inputs_v2.global_view.information")}
                  </TableCell>
                  {/*<TableCell>{"Responsável"}</TableCell>*/}
                  {monthlyStatus.map((item, key) => (
                    <TableCell key={key} style={{ textAlign: "center" }}>
                      {getDate(
                        (item.year * 100 + item.month).toString(),
                        "YYYYMM",
                        "MMM-YY",
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody className={classes.generalViewTableBody}>
                {getAllGroups().map((group) => {
                  return getInputsFromGroup(group).map((input, key) => (
                    <TableRow hover key={key}>
                      {key === 0 && (
                        <TableCell
                          style={{ backgroundColor: "#fff" }}
                          rowSpan={getInputsFromGroup(group, true)}
                        >
                          {
                            generalStatus.groups_descriptions[group][
                              i18n.language
                            ]
                          }
                        </TableCell>
                      )}
                      <TableCell
                        style={{
                          opacity:
                            filteredResponsibleUsers.length === 0 ||
                            (Array.isArray(
                              generalStatus.inputs_responsible?.[input],
                            ) &&
                              generalStatus.inputs_responsible[input].some(
                                (name) =>
                                  filteredResponsibleUsers
                                    .map((u) => u.name)
                                    .includes(name),
                              ))
                              ? "1"
                              : "0.25",
                        }}
                      >
                        {
                          generalStatus.inputs_descriptions[input].input_name[
                            i18n.language
                          ]
                        }
                      </TableCell>
                      {monthlyStatus.map((item, key) => (
                        <TableCell
                          key={key}
                          style={{
                            textAlign: "center",
                            opacity:
                              filteredResponsibleUsers.length === 0 ||
                              (Array.isArray(
                                generalStatus.inputs_responsible?.[input],
                              ) &&
                                generalStatus.inputs_responsible[input].some(
                                  (name) =>
                                    filteredResponsibleUsers
                                      .map((u) => u.name)
                                      .includes(name),
                                ))
                                ? "1"
                                : "0.15",
                          }}
                        >
                          {generalStatusTag(
                            item.year * 100 + item.month,
                            group,
                            input,
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ));
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };
  const responsibleDiv = () => {
    return (
      <Card
        style={{
          minWidth: "1200px",
          marginTop: "8px",
        }}
      >
        <CardContent
          style={{
            padding: "6px 0",
            position: "relative",
          }}
        >
          <TableContainer className={classes.generalViewTable}>
            <Table size="small" /*stickyHeader*/>
              <TableHead className={classes.generalViewTableHeader}>
                <TableRow>
                  <TableCell>
                    {t("modeling_inputs_v2.global_view.group")}
                  </TableCell>
                  <TableCell>
                    {t("modeling_inputs_v2.global_view.information")}
                  </TableCell>
                  <TableCell>{t("modeling_inputs_v2.responsible")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody className={classes.generalViewTableBody}>
                {getAllGroups().map((group) => {
                  return getInputsFromGroup(group).map((input, key) => (
                    <TableRow hover key={key}>
                      {key === 0 && (
                        <TableCell
                          style={{ backgroundColor: "#fff" }}
                          rowSpan={getInputsFromGroup(group, true)}
                        >
                          {
                            generalStatus.groups_descriptions[group][
                              i18n.language
                            ]
                          }
                        </TableCell>
                      )}
                      <TableCell
                        style={{
                          opacity:
                            filteredResponsibleUsers.length === 0 ||
                            (Array.isArray(
                              generalStatus.inputs_responsible?.[input],
                            ) &&
                              generalStatus.inputs_responsible[input].some(
                                (name) =>
                                  filteredResponsibleUsers
                                    .map((u) => u.name)
                                    .includes(name),
                              ))
                              ? "1"
                              : "0.25",
                        }}
                      >
                        {
                          generalStatus.inputs_descriptions[input].input_name[
                            i18n.language
                          ]
                        }
                      </TableCell>
                      <TableCell
                        style={{
                          opacity:
                            filteredResponsibleUsers.length === 0 ||
                            (Array.isArray(
                              generalStatus.inputs_responsible?.[input],
                            ) &&
                              generalStatus.inputs_responsible[input].some(
                                (name) =>
                                  filteredResponsibleUsers
                                    .map((u) => u.name)
                                    .includes(name),
                              ))
                              ? "1"
                              : "0.25",
                        }}
                      >
                        {Array.isArray(
                          generalStatus.inputs_responsible?.[input],
                        )
                          ? generalStatus.inputs_responsible[input].join(", ")
                          : ""}
                      </TableCell>
                    </TableRow>
                  ));
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  const lowerSpinner = (
    <div
      style={{
        marginTop: "20px",
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <ThreeDots color={clientColor} height={"40px"} />
    </div>
  );
  // endregion

  return (
    <div>
      <FloatingLoading visible={pipelineInProgress || isUploading} />
      <GridContainer style={{ marginTop: "-40px", marginBottom: "25px" }}>
        <GridItem xs={12}>
          <div
            className={cx({
              [classes.inputsCardWrapper]: true,
              ["loading"]: inputsStatus.length === 0,
            })}
          >
            <Card
              style={{
                minWidth: "1200px",
                marginBottom: "0",
                position: "relative",
              }}
            >
              <CardHeader color="client" icon>
                <CardIcon color="client">
                  <Assignment />
                </CardIcon>
                <h4 className={classes.cardIconTitle}>
                  {showCalculationLog
                    ? t("modeling_inputs_v2.calculation_log") ||
                      "Log de Cálculo"
                    : t("modeling_inputs_v2.inputs_list")}
                </h4>
                {!showCalculationLog &&
                  (!isRefreshing || monthlyStatus.length > 0) && (
                    <div className={classes.viewButtonsContainer}>
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                          verticalAlign: "middle",
                        }}
                      >
                        <Button
                          size={"sm"}
                          color={"client"}
                          simple={currentView !== "detailed"}
                          disabled={monthlyStatus.length === 0}
                          className={cx({
                            [classes.viewButton]: true,
                            [classes.firstButton]: true,
                            [classes.buttonSelected]:
                              currentView === "detailed",
                          })}
                          onClick={() => setCurrentView("detailed")}
                        >
                          {t("modeling_inputs_v2.detailed_view")}
                        </Button>
                        <Button
                          size={"sm"}
                          color={"client"}
                          simple={currentView !== "general"}
                          disabled={monthlyStatus.length === 0}
                          className={cx({
                            [classes.viewButton]: true,
                            [classes.middleButton]: true,
                            [classes.buttonSelected]: currentView === "general",
                          })}
                          onClick={() => setCurrentView("general")}
                        >
                          {t("modeling_inputs_v2.overview")}
                        </Button>
                        <Button
                          size={"sm"}
                          color={"client"}
                          simple={currentView !== "responsible"}
                          disabled={monthlyStatus.length === 0}
                          className={cx({
                            [classes.viewButton]: true,
                            [classes.lastButton]: true,
                            [classes.buttonSelected]:
                              currentView === "responsible",
                          })}
                          onClick={() => setCurrentView("responsible")}
                        >
                          {t("modeling_inputs_v2.responsible")}
                        </Button>
                      </div>
                    </div>
                  )}
              </CardHeader>
              <CardBody style={{ padding: "0 10px 10px" }}>
                <div
                  style={{
                    marginTop: "8px",
                    width: "100%",
                    padding: "0",
                  }}
                >
                  {monthlyStatus.length === 0 && (
                    <ThreeDots
                      height="20px"
                      color={clientColor}
                      ariaLabel="loading"
                      wrapperStyle={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginBottom: "30px",
                      }}
                      wrapperClass=""
                    />
                  )}
                  {currentView === "detailed" &&
                    monthlyStatus.length > 0 &&
                    monthSelectionGrid()}
                </div>
                {currentView === "detailed" &&
                  monthlyStatus.length > 0 &&
                  selectedMonthData &&
                  monthlyStatusDiv()}
              </CardBody>
            </Card>
            {/* Batch operation warning */}
            {currentView === "detailed" &&
              specialOptionsApi.activeBatchJob &&
              specialOptionsApi.isMonthInBatchRange(selectedMonthData) && (
                <div style={{ marginTop: "8px" }}>
                  <SnackbarContent
                    message={
                      <span>
                        <strong>
                          {specialOptionsApi.activeBatchJob.operation_type ===
                          "RECALCULATE"
                            ? t(
                                "modeling_inputs_v2.batch_operations.warning_title_recalculate",
                              )
                            : t(
                                "modeling_inputs_v2.batch_operations.warning_title_reset",
                              )}
                        </strong>
                        <span style={{ display: "block", marginTop: "4px" }}>
                          {t(
                            "modeling_inputs_v2.batch_operations.warning_message",
                          )}
                        </span>
                      </span>
                    }
                    color="warning"
                    paperClass={classes.batchWarningSnackbar}
                  />
                </div>
              )}
            {!showCalculationLog &&
              ((currentView === "detailed" &&
                monthlyStatus.length > 0 &&
                inputsStatus.length > 0) ||
                ((currentView === "general" || currentView === "responsible") &&
                  Object.keys(generalStatus).length > 0)) && (
                <Card style={{ marginTop: "8px", marginBottom: "0" }}>
                  <CardBody style={{ paddingTop: "8px", paddingBottom: "8px" }}>
                    <GridContainer alignItems="center">
                      <GridItem xs={"auto"}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                            minHeight: 38,
                          }}
                        >
                          <div
                            onClick={handleMyInputsToggle}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              cursor: "pointer",
                              userSelect: "none",
                            }}
                          >
                            <div
                              style={{
                                width: 28,
                                height: 14,
                                borderRadius: 7,
                                background: myInputsFilter
                                  ? clientColor
                                  : grayColor[5],
                                position: "relative",
                                transition: "background 0.2s ease",
                              }}
                            >
                              <div
                                style={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: "50%",
                                  background: "#fff",
                                  position: "absolute",
                                  top: 2,
                                  left: myInputsFilter ? 16 : 2,
                                  transition: "left 0.2s ease",
                                  boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                                }}
                              />
                            </div>
                            <span
                              style={{
                                fontSize: 12,
                                color: myInputsFilter
                                  ? grayColor[6]
                                  : grayColor[1],
                                fontWeight: 300,
                                transition: "color 0.15s ease",
                              }}
                            >
                              {t("modeling_inputs_v2.my_inputs")}
                            </span>
                          </div>
                          <div
                            onClick={handlePendingToggle}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              cursor: "pointer",
                              userSelect: "none",
                              marginRight: 16,
                            }}
                          >
                            <div
                              style={{
                                width: 28,
                                height: 14,
                                borderRadius: 7,
                                background: pendingFilter
                                  ? clientColor
                                  : grayColor[5],
                                position: "relative",
                                transition: "background 0.2s ease",
                              }}
                            >
                              <div
                                style={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: "50%",
                                  background: "#fff",
                                  position: "absolute",
                                  top: 2,
                                  left: pendingFilter ? 16 : 2,
                                  transition: "left 0.2s ease",
                                  boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                                }}
                              />
                            </div>
                            <span
                              style={{
                                fontSize: 12,
                                color: pendingFilter
                                  ? grayColor[6]
                                  : grayColor[1],
                                fontWeight: 300,
                                transition: "color 0.15s ease",
                              }}
                            >
                              {t("modeling_inputs_v2.pending_inputs")}
                            </span>
                          </div>
                        </div>
                      </GridItem>
                      <GridItem
                        style={{
                          color: grayColor[0],
                          borderLeft: "1px solid " + grayColor[5],
                          paddingLeft: "16px",
                          display: "flex",
                          alignItems: "center",
                          minHeight: 38,
                        }}
                        xs={"auto"}
                      >
                        {t("modeling_inputs_v2.filter_by_responsible") + ":"}
                      </GridItem>
                      <GridItem xs>
                        <form autoComplete={"off"}>
                          <FormControl fullWidth>
                            <ReactSelect
                              isMulti
                              options={responsibleUsers.map((user) => ({
                                value: user.id,
                                label: user.name,
                              }))}
                              value={filteredResponsibleUsers.map((user) => ({
                                value: user.id,
                                label: user.name,
                              }))}
                              onChange={(e) =>
                                handleFilterResponsibleSelectionChange(e)
                              }
                              placeholder={t("select") + "..."}
                              styles={{
                                ...selectStyles,
                                control: (provided, state) => ({
                                  ...selectStyles.control(provided, state),
                                  borderWidth: "0",
                                  marginTop: 0,
                                }),
                              }}
                              menuPortalTarget={document.body}
                            />
                          </FormControl>
                        </form>
                      </GridItem>
                    </GridContainer>
                  </CardBody>
                </Card>
              )}
            {currentView === "detailed" &&
              monthlyStatus.length > 0 &&
              inputsStatus.length > 0 &&
              !showCalculationLog &&
              inputsComponent()}
            {currentView === "general" &&
              Object.keys(generalStatus).length > 0 &&
              !showCalculationLog &&
              generalStatusDiv()}
            {currentView === "responsible" &&
              Object.keys(generalStatus).length > 0 &&
              !showCalculationLog &&
              responsibleDiv()}
            {showCalculationLog &&
              ["DEV", "IMG", "CAD"].includes(userDetails?.user_role) &&
              !(isRefreshing && !calculationLog) && (
                <Card style={{ marginTop: "8px", marginBottom: "0" }}>
                  <CardBody style={{ padding: "16px" }}>
                    {/* Placeholder: Pipeline em execução */}
                    {selectedMonthData?.status === "running" && (
                      <div
                        style={{
                          padding: "40px 20px",
                          textAlign: "center",
                          color: grayColor[1],
                        }}
                      >
                        <div style={{ marginBottom: "16px" }}>
                          <CircularProgress
                            size={40}
                            style={{ color: grayColor[2] }}
                          />
                        </div>
                        <div style={{ fontSize: "16px", fontWeight: 500 }}>
                          {t("modeling_inputs_v2.calculation_log_running") ||
                            "Pipeline em execução..."}
                        </div>
                        <div style={{ fontSize: "14px", marginTop: "8px" }}>
                          {t(
                            "modeling_inputs_v2.calculation_log_running_desc",
                          ) ||
                            "O log de cálculo será exibido após a conclusão do processamento."}
                        </div>
                      </div>
                    )}
                    {/* Placeholder: Sem log disponível */}
                    {selectedMonthData?.status !== "running" &&
                      !calculationLog && (
                        <div
                          style={{
                            padding: "40px 20px",
                            textAlign: "center",
                            color: grayColor[1],
                          }}
                        >
                          <div style={{ fontSize: "16px", fontWeight: 500 }}>
                            {t("modeling_inputs_v2.calculation_log_empty") ||
                              "Nenhum log de cálculo disponível"}
                          </div>
                          <div style={{ fontSize: "14px", marginTop: "8px" }}>
                            {t(
                              "modeling_inputs_v2.calculation_log_empty_desc",
                            ) ||
                              "O log será gerado quando o pipeline de cálculo for executado."}
                          </div>
                        </div>
                      )}
                    {/* Log disponível */}
                    {selectedMonthData?.status !== "running" &&
                      calculationLog && (
                        <>
                          {/* Aviso: Log de cálculo anterior (quando não está completo) */}
                          {selectedMonthData?.status !== "complete" && (
                            <div
                              style={{
                                padding: "12px 16px",
                                marginBottom: "16px",
                                backgroundColor: warningColor[5] || "#fff8e1",
                                borderLeft: `4px solid ${warningColor[0] || "#ff9800"}`,
                                borderRadius: "4px",
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                              }}
                            >
                              <Info
                                style={{ color: warningColor[0] || "#ff9800" }}
                              />
                              <div>
                                <div
                                  style={{
                                    fontWeight: 500,
                                    marginBottom: "4px",
                                  }}
                                >
                                  {t(
                                    "modeling_inputs_v2.calculation_log_outdated",
                                  ) || "Log de cálculo anterior"}
                                </div>
                                <div
                                  style={{
                                    fontSize: "13px",
                                    color: grayColor[0],
                                  }}
                                >
                                  {t(
                                    "modeling_inputs_v2.calculation_log_outdated_desc",
                                  ) ||
                                    "Este log refere-se ao último cálculo realizado. Será atualizado automaticamente quando um novo cálculo for concluído."}
                                </div>
                              </div>
                            </div>
                          )}
                          {calculationLog?.log_version === 2 ? (
                            <SimpleLogViewer
                              logData={calculationLog?.log_data || null}
                              inputsDict={inputsStatus.reduce((acc, group) => {
                                (group.inputs || []).forEach((input) => {
                                  acc[input.meta_name] = input;
                                });
                                return acc;
                              }, {})}
                              onInputClick={(metaName) => {
                                // Find input by meta_name and open drawer
                                for (const group of inputsStatus) {
                                  const input = (group.inputs || []).find(
                                    (inp) => inp.meta_name === metaName,
                                  );
                                  if (input) {
                                    setInputDrawerInput(input);
                                    return;
                                  }
                                }
                              }}
                            />
                          ) : (
                            <CalculationLogViewer
                              logData={calculationLog?.log_data || null}
                              inputsDict={inputsStatus.reduce((acc, group) => {
                                (group.inputs || []).forEach((input) => {
                                  acc[input.meta_name] = input;
                                });
                                return acc;
                              }, {})}
                            />
                          )}
                        </>
                      )}
                  </CardBody>
                </Card>
              )}
          </div>
          {currentView === "detailed" &&
            (!!selectedMonth || selectedMonth === 0) &&
            inputsStatus.length === 0 &&
            monthlyStatus.length > 0 &&
            !showCalculationLog &&
            lowerSpinner}
          {(currentView === "general" || currentView === "responsible") &&
            Object.keys(generalStatus).length === 0 &&
            !showCalculationLog &&
            lowerSpinner}
          {showCalculationLog &&
            ["DEV", "IMG", "CAD"].includes(userDetails?.user_role) &&
            isRefreshing &&
            !calculationLog &&
            monthlyStatus.length > 0 &&
            lowerSpinner}
        </GridItem>
      </GridContainer>
      <InputDrawer
        open={inputDrawerInput !== null}
        onClose={() => {
          if (isUploading) return;
          setInputDrawerInput(null);
        }}
        isUploading={isUploading}
        input={inputDrawerInput}
        selectedMonthData={selectedMonthData}
        pipelineInProgress={pipelineInProgress}
        getDate={getDate}
        classes={classes}
        userDetails={userDetails}
        specialOptionsApi={specialOptionsApi}
        fileInputRef={fileInputRef}
        selectStyles={selectStyles}
        usersList={usersRef.current}
        inputsStatus={inputsStatus}
        drawerUi={{
          replicatingFile,
          fileName,
          wrongDataType,
          isDragging,
          downloadButtonPopperOpen,
          downloadInProgress,
          inputDrawerShowExpectedFileContent,
          isSavingInputResponsible,
          isSavingInputDescription,
        }}
        drawerActions={{
          onDragEnter: handleDragEnter,
          onDragOver: handleDragOver,
          onDragLeave: handleDragLeave,
          onDrop: handleDrop,
          onFileSelect: handleFileSelect,
          onUploadFileClick: handleUploadFileClick,
          onReplicateFileClick: handleReplicateFileClick,
          onDownloadLastUploadedFileButton:
            handleDownloadLastUploadedFileButton,
          onRetryProcessingButtonClick: handleRetryProcessingButtonClick,
          onViewNotesClick: handleViewNotesClick,
          onClickAwayDownloadFileSelection:
            handleClickAwayDownloadFileSelection,
          onDownloadFileSelection: handleDownloadFileSelection,
          onShowExpectedFileContentClick: handleShowExpectedFileContentClick,
          onResponsibleChange: handleResponsibleSelectionChange,
          onSaveResponsible: handleSaveResponsibleButtonClick,
          onInputDetailsContentChange: setInputDetailsContentEdit,
          onSaveInputDetails: saveInputDetails,
          onConnectionClick: handleConnectionClick,
        }}
      />
      <UploadQueueDrawer
        classes={classes}
        selectStyles={selectStyles}
        getDate={getDate}
        selectedMonthData={selectedMonthData}
        inputsStatus={inputsStatus}
        queueDrawerOpen={queueDrawerOpen}
        setQueueDrawerOpen={setQueueDrawerOpen}
        isQueueActive={isQueueActive}
        setQueueRunning={setQueueRunning}
        canRunQueue={canRunQueue}
        uploadQueueItems={uploadQueueItems}
        setQueuePauseAfterCurrent={setQueuePauseAfterCurrent}
        queueIgnoreUploaded={queueIgnoreUploaded}
        setQueueIgnoreUploaded={setQueueIgnoreUploaded}
        clearQueue={clearQueue}
        queueFolderInputRef={queueFolderInputRef}
        handleQueueFolderSelected={handleQueueFolderSelected}
        queueSelectResetKey={queueSelectResetKey}
        queueNewSelectedInputId={queueNewSelectedInputId}
        setQueueNewSelectedInputId={setQueueNewSelectedInputId}
        handleQueuePickFile={handleQueuePickFile}
        queueFileInputRef={queueFileInputRef}
        queuePendingInputIdRef={queuePendingInputIdRef}
        addItemToQueue={addItemToQueue}
        setQueueSelectResetKey={setQueueSelectResetKey}
        moveItemUp={moveItemUp}
        moveItemDown={moveItemDown}
        removeItemFromQueue={removeItemFromQueue}
      />
      {inputDrawerInput && (
        <InputNotesDialog
          open={displayNotesModalOpen}
          onClose={() => setDisplayNotesModalOpen(false)}
          input={inputDrawerInput}
          verifyingNoteUuids={inputControlsMarkedAsVerifiedLine}
          onMarkAsAnalyzed={handleMarkAsAnalyzedClick}
          onValuesCopied={notifyCopy}
          classes={classes}
          Transition={Transition}
        />
      )}
      <ResetInputsDialog
        open={resetInputsModalOpen}
        onClose={() => setResetInputsModalOpen(false)}
        onConfirm={handleConfirmResetInput}
        disableActions={inputsStatus.length === 0}
        classes={classes}
        Transition={Transition}
      />
      <SpecialOptionsModal
        api={specialOptionsApi}
        classes={classes}
        Transition={Transition}
        selectStyles={selectStyles}
        userDetails={userDetails}
        clientColor={clientColor}
      />
    </div>
  );
}
